/**
 * Pure display helpers, verbatim from the mock — TASK 8.1.
 *
 * These three lived in `modules/app/Shell.js` while it was their only caller
 * (TASK 7.2). Story 8 gives them four more — the browse rows, the album header,
 * the full player and the seek bar — and one of those (`views/player/
 * FullPlayer.js`) is imported BY the shell, so leaving them there would have
 * made the import graph cyclic. Nothing about them is shell-specific; they are
 * formatting, so they live with the other primitives. `Shell.js` re-exports
 * them, keeping its published surface unchanged.
 *
 * @module
 */

/** mock L889 — FNV-1a over the id, the seed for the cover gradient. */
function hashOf(value) {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * mock L890 `_grad(id)` — the deterministic cover gradient.
 *
 * @param {string} id
 * @returns {string}
 */
export function gradientFor(id) {
  const h = hashOf(id);
  const a = h % 360;
  const b = (a + 52 + ((h >> 4) % 140)) % 360;
  return `linear-gradient(135deg,hsl(${a} 82% 63%),hsl(${b} 80% 55%))`;
}

/**
 * The real cover-art URL for an album, or `undefined` when it has none.
 *
 * "Has real art" is EXACTLY: `coverRef` is a non-empty string beginning
 * `covers/` — the server writes that ref only once a real cover exists, and
 * leaves `null` / `"placeholder"` otherwise. For those the caller keeps the
 * generated gradient + initials (the intended art-less look, {@link gradientFor}
 * + {@link initialsOf}), so this returns `undefined` and no image layer is
 * built. The URL points at `GET /api/art/:albumId`, which the service worker
 * serves from cache offline — the SAME endpoint the OS Media Session and the
 * download manager already use, so a tile never fetches art the app hasn't.
 *
 * This is the ONE place the has-real-art rule lives, so the cover tiles
 * ({@link import('./Card.js').Card}/AlbumGridCard) and the album-detail hero
 * can never disagree about which albums show a real cover.
 *
 * @param {{ id: string, coverRef: string | null }} album
 * @returns {string | undefined}
 */
export function albumArtUrl(album) {
  return typeof album.coverRef === 'string' && album.coverRef.startsWith('covers/')
    ? `/api/art/${encodeURIComponent(album.id)}`
    : undefined;
}

/**
 * The real photo URL for an artist, or `undefined` when it has none.
 *
 * The EXACT mirror of {@link albumArtUrl} for artists: "has a photo" is
 * `imageRef` being a string beginning `artists/` — the server writes that ref
 * only once a real image exists and leaves `null` otherwise. For those the
 * caller keeps the generated gradient + initials (the intended art-less look),
 * so this returns `undefined` and no image layer is built. The URL points at
 * `GET /api/artist-image/:artistId`, authz-scoped the same as `/api/art`.
 *
 * This is the ONE place the has-a-photo rule lives, so every artist avatar
 * (browse rows, any future detail header) can never disagree about which
 * artists show a real photo.
 *
 * @param {{ id: string, imageRef?: string | null }} artist
 * @returns {string | undefined}
 */
export function artistImageUrl(artist) {
  return typeof artist.imageRef === 'string' && artist.imageRef.startsWith('artists/')
    ? `/api/artist-image/${encodeURIComponent(artist.id)}`
    : undefined;
}

/**
 * mock L891 `_ini(t)` — up to two initials from a title.
 *
 * @param {string} title
 * @returns {string}
 */
export function initialsOf(title) {
  if (title === '') return '?';
  const words = title
    .replace(/[^\w\s]/g, '')
    .trim()
    .split(/\s+/);
  return `${(words[0] ?? '')[0] ?? ''}${(words[1] ?? '')[0] ?? ''}` || '?';
}

/**
 * mock L892 `_fmt(s)` — `m:ss`.
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatSeconds(seconds) {
  const total = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * `h:mm:ss` for a whole-album/playlist duration, falling back to `m:ss` under
 * an hour. The mock only ever ran `_fmt` over album totals, which printed
 * "78:55" for a 78-minute album; ui-contract §10.6's own album-detail example
 * ("…5 tracks · 18:55") is under an hour, so this is a strict superset that
 * changes no captured string.
 *
 * @param {number} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number.isFinite(seconds) ? seconds : 0));
  if (total < 3600) return formatSeconds(total);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
